// src/pages/user/memoir/layout.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  BookIcon,
  PlusIcon,
  LogoIcon,
  SearchIcon,
  NotificationIcon,
  LogoutIcon,
  ArrowLeft,
  MenuIcon,
  CloseIcon
} from '../../../public/icons';
import { memoirService } from '../../../services/memoirService';

const MemiorLayout = ({ children, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [memoirHistory, setMemoirHistory] = useState([]);
  const [loadingMemoirs, setLoadingMemoirs] = useState(true);
  const [memoirsError, setMemoirsError] = useState('');

  /* ── Load user's memoirs for the sidebar ── */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingMemoirs(true);
        setMemoirsError('');

        const res = await memoirService.getMyMemoirs();

        if (cancelled) return;

        if (res?.success) {
          setMemoirHistory(res.data || []);
        } else {
          setMemoirHistory([]);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load memoirs:', err);
        setMemoirsError(err.message || 'Failed to load memoirs');
        setMemoirHistory([]);
      } finally {
        if (!cancelled) setLoadingMemoirs(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.removeItem('kapta_token');
      localStorage.removeItem('kapta_user');
      navigate('/login');
    }
  };

  /* ── Status badge styling ── */
  const getStatusStyle = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'active' || normalized === 'in progress' || normalized === 'processing') {
      return 'text-yellow-300';
    }
    if (normalized === 'completed' || normalized === 'published') {
      return 'text-green-300';
    }
    if (normalized === 'cancelled') {
      return 'text-red-300';
    }
    return 'text-[#8A9099]';
  };

  /* ── Sidebar content ── */
  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-4 sm:px-6 mt-6 sm:mt-10 mb-6 sm:mb-8">
        <LogoIcon color="#FCF7EE" size={28} />
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden text-white hover:text-gray-300"
        >
          <CloseIcon size={24} color="#FCF7EE" />
        </button>
      </div>

      {/* My Memoir Section */}
      <div className="px-3 sm:px-4 mt-6 sm:mt-8">
        <h3 className="text-[#8A9099] text-xs sm:text-sm text-left font-medium mb-3 sm:mb-4 px-2 sm:px-4">
          My Memoir
        </h3>

        {/* Loading */}
        {loadingMemoirs && (
          <div className="px-4 py-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8A9099] mx-auto" />
            <p className="mt-2 text-xs text-[#8A9099]">Loading memoirs…</p>
          </div>
        )}

        {/* Error */}
        {!loadingMemoirs && memoirsError && (
          <div className="px-4 py-4 text-xs text-red-300 text-left">
            {memoirsError}
          </div>
        )}

        {/* Empty state */}
        {!loadingMemoirs && !memoirsError && memoirHistory.length === 0 && (
          <div className="px-4 py-6 text-left">
            <p className="text-xs text-[#8A9099] mb-3">
              You haven't started a memoir yet.
            </p>
            <Link
              to="/memoirs/create"
              onClick={() => setSidebarOpen(false)}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#FCF7EE] bg-[#162A55] hover:bg-[#1E3A6F] rounded-lg transition-colors"
            >
              <PlusIcon size={14} color="#FCF7EE" />
              Start a Memoir
            </Link>
          </div>
        )}

        {/* Memoir list */}
        {!loadingMemoirs && memoirHistory.length > 0 && (
          <div className="space-y-2">
            {memoirHistory.map((memoir) => {
              const isActive = location.pathname.includes(
                `/user/memoir/${memoir.id}`
              );

              return (
                <Link
                  key={memoir.id}
                  to={`/user/memoir/${memoir.id}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`block p-3 sm:p-4 transition-colors duration-200 rounded-lg ${
                    isActive
                      ? 'bg-[#162A55] text-[#F7F5EF]'
                      : 'text-[#8A9099] hover:bg-[#162A55] hover:text-[#F7F5EF]'
                  }`}
                >
                  <div className="text-left">
                    <h4 className="font-normal text-sm sm:text-base text-[#FCF7EE] leading-tight mb-1 sm:mb-2 line-clamp-2">
                      {memoir.title}
                    </h4>
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-[#8A9099] font-normal">
                        {memoir.chapters || memoir.totalWeeks} chapters
                      </span>
                      <span
                        className={`px-2 font-normal py-0.5 sm:py-1 rounded-full text-xs sm:text-sm ${getStatusStyle(
                          memoir.status
                        )}`}
                      >
                        {memoir.statusLabel || memoir.status}
                      </span>
                    </div>
                    {typeof memoir.completedWeeks === 'number' && (
                      <p className="mt-1 text-[10px] sm:text-xs text-[#8A9099]">
                        Week {memoir.completedWeeks} of {memoir.totalWeeks}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Back to Dashboard */}
      <div className="absolute bottom-6 sm:bottom-8 left-0 right-0 px-3 sm:px-4">
        <Link
          to="/account/profile"
          onClick={() => setSidebarOpen(false)}
          className="w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-lg transition-colors duration-200 text-[#8A9099] hover:bg-[#162A55] hover:text-[#F7F5EF]"
        >
          <ArrowLeft size={18} color="#8A9099" />
          <span className="font-medium">Back to Dashboard</span>
        </Link>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FCF7EE]">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#0F1F3D] rounded-lg text-white shadow-lg"
      >
        <MenuIcon size={24} color="#FCF7EE" />
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-40 w-72 sm:w-80 bg-[#0F1F3D]
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 overflow-y-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="relative min-h-full pb-24">
          <SidebarContent />
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ml-72 lg:ml-80 min-h-screen">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-20 bg-[#FCF7EE] border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-center">
            <h1 className="text-lg font-semibold text-[#1C2A46]">My Memoir</h1>
          </div>
        </div>

        {/* Page content */}
        <main className="px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MemiorLayout;